import { registerPlugin, type PluginListenerHandle } from '@capacitor/core'

export interface StoreProdukt {
  produktId: string
  name?: string
  beschreibung?: string
  basisplanId: string
  angebotId: string | null
  angebotToken: string
  formattedPrice: string
  waehrung: string
  betragMikro: number
  abrechnungszeitraum: string
  wiederholung: number
}

export interface StoreKauf {
  purchaseToken: string
  produktId: string | null
  produktIds: string[]
  basisplanId: string | null
  bestellnummer: string | null
  istBestaetigt: boolean
  istTestkauf: boolean | null
  kaufzustand: number
  kaufzeit: number
  automatischeVerlaengerung: boolean
  kontoKennung: string | null
  originalJson: string
  /**
   * Nur iOS (Vorgang 009, Schnitt 8): die von Apple signierte Transaktion
   * (`jwsRepresentation`). Sie — und nur sie — ist der Nachweis, den
   * `apple-purchase` prüft; der Client behauptet nichts (AK33). Auf Android
   * fehlt das Feld, dort ist `purchaseToken` der Griff.
   */
  jws?: string
  /** Nur iOS: `Sandbox` | `Production` | `Xcode` — für die AS3-Kennzeichnung. */
  umgebung?: string
}

export interface StoreAntwort<T> {
  responseCode: number
  debugMessage: string
  phase?: string
  produkte?: StoreProdukt[]
  nichtGeladen?: Array<{ produktId: string; statusCode: number }>
  kaeufe?: T[]
  /** Nur iOS: Länderkodex der Storefront, DREIBUCHSTABIG (`AUT`) — StoreKit
   *  liefert alpha-3; die Normalisierung auf `AT` ist Serversache (A8.5). */
  storefront?: string
}

interface StaySuiteStorePlugin {
  addListener(event: 'kaufAktualisiert', listener: (kauf: StoreKauf) => void): Promise<PluginListenerHandle>
  produkte(options: { produktIds: string[] }): Promise<StoreAntwort<never>>
  kaufen(options: {
    produktId: string
    basisplanId: string
    kontoKennung: string
  }): Promise<StoreAntwort<StoreKauf>>
  aktiveKaeufe(options?: { interaktiv?: boolean }): Promise<StoreAntwort<StoreKauf>>
  transaktionBeenden(options: { transaktionId: string; jws: string }): Promise<void>
  /**
   * Nur iOS: öffnet Apples eigenes Verwaltungsblatt
   * (`AppStore.showManageSubscriptions`). Die Kündigung eines Apple-Abos
   * geschieht ausschließlich bei Apple — die App zeigt den Weg, statt ihn zu
   * verstecken (dieselbe Aussage wie `konto.aboApple` in Vorgang 019).
   * Auf Android nicht implementiert; der Aufruf wird dort abgewiesen.
   */
  aboVerwalten(): Promise<void>
}

export const StaySuiteStore = registerPlugin<StaySuiteStorePlugin>('StaySuiteStore')

export function storeSandboxSichtbar({
  dev,
  sandboxBuild,
  native,
  platform,
}: {
  dev: boolean
  sandboxBuild: boolean
  native: boolean
  platform: string
}): boolean {
  return (dev || sandboxBuild) && native && platform === 'android'
}

export async function schnitt1KontoKennung(): Promise<string> {
  const bytes = new TextEncoder().encode('staysuite:vorgang-009:schnitt-1:ladenprobe')
  const digest = await crypto.subtle.digest('SHA-256', bytes)
  return [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, '0')).join('')
}
